import { useSignal } from "@preact/signals-react";
import { format, addDays, parse } from "date-fns";
import { FormattedMessage as FM } from "react-intl";

import DatePicker from "componentsv2/form/DatePicker";
import Dropdown from "componentsv2/form/Dropdown";
import NoDataSheet from "./NoDataSheet";
import WizardUpdate from "./WizardUpdate";
import { TableListIcon } from "components/Icons";
import ModalWizard from "componentsv2/modal/ModalWizard";
import proxy from "api/proxy";
import Sheet from "./Sheet";
import { useSheet } from "./useSheet";
import { useWizard } from "storev2/wizardStore";

function RateCalendar() {
  const { store } = useWizard();

  const selectedDate = useSignal(format(new Date(), "yyyy-MM-dd"));
  const selectedOC = useSignal(null);
  const selectedRatePlan = useSignal(null);
  const buttonVisible = useSignal(false);
  const dataWizard = useSignal(null);
  const start_date = useSignal(null);
  const end_date = useSignal(null);

  const args = {
    start_date: selectedDate.value,
    oc: selectedOC.value,
    rate_plan: selectedRatePlan.value,
  };
  const { isLoading, data, error, refetch } = useSheet(args);

  async function onSave(value) {
    const model = "hotel.calendar.line.price";
    const storecRec = {
      id: value.id,
      unit_price: value.unit_price,
    };
    if (value.id) {
      const values = {
        model: model,
        storeRec: storecRec,
      };
      await proxy.saveQuery(values);
    }
  }

  function onChange() {
    refetch();
    if (selectedRatePlan.value && selectedOC.value) {
      buttonVisible.value = true;
    }
  }

  function handleReset() {
    refetch();
    if (selectedRatePlan.value && selectedOC.value) {
      buttonVisible.value = true;
    }
    dataWizard.value = null;
    start_date.value = null;
    end_date.value = null;
  }

  const attrsDate = {
    label: "hotel.calendar.select_date",
    name: "calendar_date",
    labeled: false,
    required: true,
    onChange: onChange,
    data: selectedDate,
  };

  const attrsRatePlan = {
    field: {
      name: "rate_plan",
      model: "hotel.rate_plan",
      type: "many2one",
      required: true,
    },
    label: "hotel.calendar.rate_plan",
    name: "rate_plan",
    type: "many2one",
    model: "hotel.rate_plan",
    onChange: onChange,
    searchable: true,
    required: true,
    data: selectedRatePlan,
  };

  const attrsOC = {
    field: {
      name: "operation_center",
      model: "company.operation_center",
      type: "many2one",
      required: true,
    },
    label: "hotel.calendar.company.operation_center",
    name: "operation_center",
    onChange: onChange,
    searchable: true,
    data: selectedOC,
  };

  let msg = "no_data_available";
  if (isLoading) {
    msg = "is_loading";
  } else if (error) {
    msg = "error_no_calendar";
  }

  const wizardUpdate = {
    label: "hotel.calendar.update",
    model: "hotel.calendar.update",
    icon: <TableListIcon className="w-5 h-5 fill-current text-neutral-700" />,
    Component: WizardUpdate,
    color: "amber",
    args: {
      data: dataWizard,
      rate_plan: selectedRatePlan,
      operation_center: selectedOC,
      start_date: start_date,
      end_date: end_date,
    },
    buttons: [
      { name: "hotel.calendar.update", onClick: onClickUpdate, color: "black" },
    ],
  };

  async function onClickUpdate() {
    const model = "hotel.calendar.line.price";
    const fields_names = ["id"];
    const range = [];
    let nextDate = parse(store.start_date, "yyyy-MM-dd", new Date());
    const endDate = parse(store.end_date, "yyyy-MM-dd", new Date());
    while (nextDate <= endDate) {
      range.push(format(nextDate, "yyyy-MM-dd"));
      nextDate = addDays(nextDate, 1);
    }
    for (const rec of store.rates.values()) {
      if (!rec.unit_price || rec.unit_price <= 0) continue;
      const domain = [
        ["line.calendar.rack_date", "in", range],
        ["line.product", "=", rec.accommodation.id],
        ["price_list", "=", rec.price_list.id],
        ["line.calendar.calendar.rate_plan", "=", selectedRatePlan.value.id],
        ["line.calendar.calendar.operation_center", "=", selectedOC.value.id],
      ];
      const linePriceIds = await proxy.search(model, domain, fields_names);
      const values = { unit_price: rec.unit_price };
      const ids = linePriceIds.data.map((value) => value.id);
      const args = { model, ids, values };
      await proxy.save(args);
    }
    refetch();
  }

  return (
    <div className="w-full h-full px-4 overflow-x-hidden">
      <div className="flex pb-4">
        <h2 className="flex text-3xl font-bold text-sky-700 p-3 my-auto">
          <FM id="hotel.calendar.rates" />
        </h2>
        <div className="flex pl-12 gap-x-4">
          <Dropdown {...attrsOC} />
          <DatePicker {...attrsDate} />
          <Dropdown {...attrsRatePlan} />
          {buttonVisible.value && (
            <ModalWizard {...wizardUpdate} style="w-48" onReset={handleReset} />
          )}
        </div>
      </div>
      {data ? <Sheet onSave={onSave} data={data} /> : <NoDataSheet msg={msg} />}
    </div>
  );
}

export default RateCalendar;
